import type { MeResponse } from '@/auth/useSession';

/**
 * Resolves a field's `sourceValue` key against the signed-in advisor's profile.
 *
 * The key set here MUST stay in lock-step with the domain allow-list
 * (`WorkflowField.IsValidSourceValue`) and the originator's picker in
 * `FieldPropertiesPanel`. An unknown key returns null and the runtime silently
 * leaves the field empty, which reads as "prefill mysteriously stopped
 * working" — the domain is what stops a bad key ever reaching here.
 *
 * Name + email come off the Entra/ISAM session; phone, dealer code and rep
 * codes come off the GAMX advisor-info lookup performed at ISAM login, so they
 * are absent for originator/approver sessions and for advisors whose lookup
 * failed. Returning null in that case is deliberate — the caller only writes to
 * empty slots, so a missing profile never blanks something the advisor typed.
 *
 * @returns the value to prefill, or null for "nothing to fill".
 */
export function resolveAdvisorSourceValue(key: string, user: MeResponse): string | null {
  switch (key) {
    case 'advisor.fullName':
      return user.name ?? null;
    case 'advisor.email':
      return user.email ?? null;
    case 'advisor.phone':
      return user.phoneNumber ?? null;
    case 'advisor.dealerCode':
      return user.dealerCode ?? null;
    case 'advisor.repCode':
      // Only auto-fill when the advisor has exactly ONE rep code. With
      // several, AC3 says the advisor picks — FieldRenderer swaps the
      // read-only input for a Select, and prefilling here would silently
      // decide for them (the slot would be non-empty, so the picker would
      // open on an arbitrary code that the advisor never chose).
      return user.representativeCodes?.length === 1 ? (user.representativeCodes[0] ?? null) : null;
    default:
      return null;
  }
}

/**
 * True when the advisor carries several representative codes and therefore has
 * to choose one (AC3). One code, none, or a non-advisor session all read as
 * false — the field then renders as the plain read-only prefilled input.
 */
export function needsRepCodeChoice(user: MeResponse | null | undefined): boolean {
  return (user?.representativeCodes?.length ?? 0) > 1;
}

/**
 * True when this field is the rep-code slot AND the advisor actually has a
 * choice to make — i.e. it renders as a Select the advisor must answer, not as
 * a prefilled read-only input.
 *
 * Required + read-only fields are normally exempt from the empty check, because
 * the platform supplies their value and an exempt-less gate would block Next
 * while the session resolver is still in flight. This field is the one
 * exception: the resolver deliberately declines to choose (see
 * resolveAdvisorSourceValue), so nothing will ever arrive to fill it and the
 * exemption would let an advisor submit with no rep code at all.
 */
export function isAdvisorRepCodeChoice(
  field: { sourceValue?: string | null },
  user: MeResponse | null | undefined,
): boolean {
  return field.sourceValue === 'advisor.repCode' && needsRepCodeChoice(user);
}

/**
 * Builds the "skip the required-empty check for this field" predicate for a
 * given session. Read-only ⇒ exempt, except for the rep-code picker.
 *
 * A factory rather than a 2-arg predicate because the completeness walkers are
 * module-level pure functions that call it once per field — binding the session
 * once keeps the hot loop free of the extra argument.
 */
export function requiredExemptionFor(
  user: MeResponse | null | undefined,
): (field: { readOnly?: boolean | null; sourceValue?: string | null }) => boolean {
  return (field) => field.readOnly === true && !isAdvisorRepCodeChoice(field, user);
}
